import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { FiPlus } from "react-icons/fi";
import { colors } from "../theme/modernTheme";
import DonationSchemaDialog from "./DonationSchemaDialog";
import { useAuth } from "../context/AuthContext";

/**
 * Autocomplete component for donation schema selection with "Add Schema" functionality
 * and infinite scroll support.
 *
 * @param {Array} options - Available donation schemas to select from
 * @param {Object|null} value - Currently selected schema (single object, not array)
 * @param {Function} onChange - Callback when selection changes (event, newValue)
 * @param {Function} onSchemaCreated - Optional callback when a new schema is created
 * @param {String} placeholder - Placeholder text
 * @param {Object} sx - Additional styles for the TextField
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} error - Whether field has error
 * @param {String} helperText - Helper text to display
 * @param {String} name - Field name
 * @param {Boolean} loading - Whether schemas are loading
 * @param {Boolean} hasMore - Whether there are more schemas to load
 * @param {Function} onLoadMore - Callback to load more schemas
 */
export default function DonationSchemaAutocomplete({
  options = [],
  value = null,
  onChange,
  onSchemaCreated,
  placeholder = "Select donation schema",
  sx = {},
  required = false,
  error = false,
  helperText = "",
  name = "donation_schema",
  loading = false,
  hasMore = false,
  onLoadMore = () => {},
}) {
  const { hasModulePermission, MODULE_PERMISSIONS } = useAuth();
  const donationSchemasPermission = hasModulePermission(
    MODULE_PERMISSIONS.DONATION_SCHEME
  );
  console.log("donationSchemasPermission", donationSchemasPermission);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [availableSchemas, setAvailableSchemas] = useState([]);

  // Combine special "Add New" option with actual options
  useEffect(() => {
    setAvailableSchemas(options);
  }, [options]);

  const handleOpenSchemaDialog = () => {
    setSchemaDialogOpen(true);
  };

  const handleSchemaDialogClose = () => {
    setSchemaDialogOpen(false);
  };

  const handleSchemaCreated = async (createdSchema) => {
    // Reload schemas from API - logic handled by parent via callback usually,
    // but here we might need to refresh the list or just append.
    // The previous implementation fetched all. Here we should probably let the parent handle it
    // OR we just locally invoke the success behavior.

    // For consistency with previous behavior, we'll invoke the creation callback
    // and ideally the parent refreshes the list or adds it.

    // However, since we are moving to infinite scroll controlled by parent,
    // we should rely on onSchemaCreated to inform parent to reload/append.

    if (onSchemaCreated) {
      onSchemaCreated(createdSchema);
    }

    // Auto-select the new schema
    const syntheticEvent = {
      target: {
        name: name,
        value: createdSchema,
      },
    };
    onChange && onChange(syntheticEvent, createdSchema);
  };

  const handleChange = (event, newValue) => {
    // Handle "Add New Schema" selection
    if (
      newValue &&
      newValue.id === "__add_new__" &&
      donationSchemasPermission
    ) {
      handleOpenSchemaDialog();
      return;
    }

    const syntheticEvent = {
      target: {
        name: name,
        value: newValue,
      },
    };
    onChange && onChange(syntheticEvent, newValue);
  };

  // Custom option renderer to style "Add New Schema" differently
  const renderOption = (props, option) => {
    // Extract key from props to avoid React warning, pass rest to li
    const { key, ...liProps } = props;

    if (option.id === "__add_new__" && donationSchemasPermission) {
      return (
        <li
          key={key}
          {...liProps}
          style={{
            ...liProps.style,
            borderBottom: `1px solid ${colors.border.main}`,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: colors.primary.main,
              fontWeight: 600,
              width: "100%",
              py: 1,
            }}>
            <FiPlus size={18} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
              Add New Schema
            </Typography>
          </Box>
        </li>
      );
    }
    return (
      <li key={key} {...liProps}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: colors.text.primary,
          }}>
          {option.name}
        </Typography>
      </li>
    );
  };

  // Filter options to ensure "Add New" is always present
  const filterOptions = (options, params) => {
    const { inputValue } = params;
    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Always prepend "Add New" option
    return [
      ...(donationSchemasPermission
        ? [{ id: "__add_new__", name: "Add New Schema" }]
        : []),
      ...filtered,
    ];
  };

  return (
    <>
      <Autocomplete
        value={value}
        onChange={handleChange}
        options={availableSchemas}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={filterOptions}
        renderOption={renderOption}
        loading={loading}
        sx={sx}
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            if (
              !loading &&
              hasMore &&
              listboxNode.scrollTop + listboxNode.clientHeight >=
                listboxNode.scrollHeight - 50
            ) {
              onLoadMore();
            }
          },
          style: {
            maxHeight: 250,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label="Donation Schemes"
            required={required}
            error={error}
            helperText={helperText}
            FormHelperTextProps={{
              sx: { marginLeft: 0 },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <DonationSchemaDialog
        open={schemaDialogOpen}
        onClose={handleSchemaDialogClose}
        schema={null}
        onSuccess={handleSchemaCreated}
      />
    </>
  );
}
